/**
 * What the Campaigns screen says, as finished sentences.
 *
 * Out of `Campaigns.tsx` so a runner can read it. Two of these are the contract's
 * own words for the answers after which a retry is wrong or unsafe, and
 * `campaign-copy.test.ts` checks them against docs/API-CONTRACT.md.
 *
 * Nothing here says how much may be paid out. A budget is shown as TikTok reports
 * it, in the account's currency, with no arithmetic: the contract passes the float
 * through unconverted, and so does the screen.
 */
import type { Campaign, CampaignPage, TikTokAccount } from "./api.ts";
import type { CreateOutcome, StatusOutcome } from "./campaigns.ts";
import type { TikTokHost } from "./tiktok-host.ts";

export interface Banner {
	title: string;
	body: string;
}

/** GET /campaigns said `truncated`: TikTok counts more than one page holds. */
export const FLOOR_BANNER: Banner = {
	title: "These campaigns are floors, not totals.",
	body: "TikTok counts more campaigns under this ad account than one page holds, so this list is the first 1000. Nothing was filtered out.",
};

/** `created: true`, in the contract's words. */
export const EXISTS: Banner = {
	title: "The campaign exists — do not retry, look it up.",
	body: "created: true means the object exists, whatever else the response says; do not retry — a retry creates a second campaign. Look the id up.",
};

/** `created: "unknown"`, or a create that got no answer at all, in the contract's words. */
export const UNKNOWN: Banner = {
	title: "It may have been created — do not retry yet.",
	body: "The create may have gone through; list the advertiser's campaigns (by name) before retrying.",
};

export const READ_ONLY =
	"You can read these campaigns. Creating or switching them needs write access on this ad account.";

/**
 * Where the reader is in the account: TikTok's own `page_info`, said plainly. The count is
 * every campaign under the ad account, not the rows on this page.
 */
export function pagerLabel(p: CampaignPage): string {
	const campaigns = `${p.totalNumber} campaign${p.totalNumber === 1 ? "" : "s"}`;
	return `Page ${p.page} of ${p.totalPages} · ${campaigns} under this ad account`;
}

/** A campaign whose secondary status TikTok did not send. */
export const NOT_REPORTED = "Not reported";

export function createdText(o: Extract<CreateOutcome, { kind: "created" }>): string {
	return `Created ${o.operationStatus}, id ${o.id}. It stays off until someone turns it on.`;
}

export function mismatchText(o: Extract<StatusOutcome, { kind: "mismatch" }>): string {
	const reported = o.operationStatus ?? "no such campaign";
	const doing = o.secondaryStatus ? ` (${o.secondaryStatus})` : "";
	return `Asked for ${o.asked}; TikTok reports ${reported}${doing}.`;
}

/**
 * A budget cell: the number as TikTok reported it, the account's currency, and the
 * mode that says what the number means. `BUDGET_MODE_INFINITE` carries no
 * meaningful number, so none is shown beside it.
 */
export function budgetText(
	c: Pick<Campaign, "budget" | "budgetMode">,
	currency: string | null,
): string {
	if (c.budgetMode === "BUDGET_MODE_INFINITE") return c.budgetMode;
	return `${String(c.budget)}${currency ? ` ${currency}` : ""} · ${c.budgetMode}`;
}

/**
 * Under Create, while it is locked after `exists` or `unknown`.
 *
 * 🔴 It used to say "reload the list and look for this campaign by name". On X that
 * was measured unfollowable — the list lags behind a create by minutes, so in
 * exactly those minutes the campaign is missing from it (twads#86), and the reload
 * lifted the lock anyway (twads#87). **For TikTok nobody has measured whether the
 * list lags**, and that is the reason to change the words here rather than a reason
 * to wait: an instruction that is safe only if an unmeasured platform is fast is
 * not a safe instruction. The old copy was right and possibly unfollowable at once
 * — the worst kind, because nothing about it reads wrong.
 */
export const CREATE_LOCKED =
	"Create is locked: this campaign may already exist. A new campaign can take time to appear in TikTok's list, so not seeing it here does not mean it was not created.";

/** The deliberate way out of the lock, for the answer that carries no id. */
export const CREATE_ANYWAY = {
	label: "Create another anyway",
	title: "This may create a second campaign.",
	body:
		"Nothing here can tell you whether the first one exists: the answer carried no id, and a " +
		"new campaign may not show in TikTok's list yet. Check the advertiser in TikTok Ads Manager " +
		"before you do this. A second campaign cannot be deleted from this console.",
	confirm: "I checked in TikTok — create another",
};

/**
 * Said in the same place, because a lock someone cannot get past is a lock they
 * route around: this one lives in the page and a browser reload clears it. Better
 * that they learn it here, beside the warning, than discover it by pressing F5 and
 * never see the warning again.
 */
export const CREATE_LOCK_IS_LOCAL =
	"Reloading this page clears the warning — it is kept in the screen, not in the advertiser.";

/**
 * What someone confirms before turning a campaign on — the one action on this
 * screen after which TikTok can start delivering. It names the campaign, the ad
 * account, the budget as TikTok reports it, and the TikTok host the switch goes to.
 * A host it cannot name — not read yet, or not one of TikTok's two — is shown to be
 * treated as production. Turning a campaign off asks nothing.
 */
export function enablePrompt(
	c: Pick<Campaign, "name" | "budget" | "budgetMode">,
	account: Pick<TikTokAccount, "name" | "advertiserId">,
	currency: string | null,
	host: TikTokHost,
): string {
	const where =
		host.kind === "unknown"
			? "unknown — treat it as production"
			: host.kind === "other"
				? `${host.host} (not a TikTok host this screen knows) — treat it as production`
				: host.text;
	return `Turn on "${c.name}" under ${account.name} · ${account.advertiserId}? Budget: ${budgetText(c, currency)}. TikTok host: ${where}. It can start delivering as soon as TikTok allows it.`;
}

/**
 * What stands beside Create when a campaign made there would be a real one, or when
 * the screen cannot tell. An unknown host is not taken as safe: until the smoke
 * check names it, a write is assumed to go to production. A host with no label is no
 * better known, and is treated the same way. Only the sandbox adds nothing.
 */
export function hostWarning(host: TikTokHost): Banner | null {
	if (host.kind === "production") {
		return {
			title: "Writes here go to TikTok production.",
			body: `${host.host}: a campaign created on this screen is created on the real ad account.`,
		};
	}
	if (host.kind === "unknown") {
		return {
			title: "Which TikTok this screen writes to could not be read — treat it as production.",
			body: "The Systems check on Overview names the host once it answers.",
		};
	}
	if (host.kind === "other") {
		return {
			title: "This screen writes to a host it does not know — treat it as production.",
			body: `${host.host}: neither TikTok's production host nor its sandbox host.`,
		};
	}
	return null;
}
